import { useEffect } from "react";
import { ArrowLeft, Mail, AlertTriangle, ArrowRight, Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SITE } from "@/data/site";

export default function LegalPage({ doc }) {
  useEffect(() => {
    document.title = `${doc.title} — Warranty_Vault pro`;
  }, [doc]);

  return (
    <main className="relative min-h-screen pt-28" data-testid={`legal-${doc.key}`}>
      <div className="vault-grid-bg grain absolute inset-x-0 top-0 h-72" />
      <div className="relative mx-auto max-w-3xl px-5 pb-10 sm:px-8">
        <a
          href="/"
          data-testid="legal-back"
          className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-gold"
        >
          <ArrowLeft className="h-4 w-4" /> Back to home
        </a>

        <Reveal className="mt-6">
          <p className="eyebrow">Legal & Support</p>
          <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight sm:text-5xl">{doc.title}</h1>
          {doc.subtitle && <p className="mt-4 text-lg text-muted-foreground">{doc.subtitle}</p>}
          {doc.updated && <p className="mt-2 text-xs uppercase tracking-wide text-muted-foreground/80">Last updated: {doc.updated}</p>}
          <div className="gold-hairline mt-8 h-px w-full" />
        </Reveal>

        <article className="mt-8 space-y-6">
          {doc.blocks.map((b, i) => (
            <Block key={`${b.t}-${i}`} block={b} />
          ))}
        </article>

        <div className="mt-14 rounded-2xl border border-border bg-card/50 p-6">
          <p className="text-sm text-muted-foreground">
            Need more help? Email{" "}
            <a href={`mailto:${SITE.supportEmail}`} className="font-medium text-gold hover:underline">
              {SITE.supportEmail}
            </a>
          </p>
        </div>
      </div>
    </main>
  );
}

function Block({ block }) {
  switch (block.t) {
    case "h":
      return <h2 className="font-display text-xl font-semibold tracking-tight text-foreground sm:text-2xl">{block.text}</h2>;
    case "p":
      return <p className="text-[15px] leading-relaxed text-muted-foreground">{block.text}</p>;
    case "ul":
      return (
        <ul className="space-y-2.5">
          {block.items.map((it) => (
            <li key={it} className="flex items-start gap-2.5 text-[15px] leading-relaxed text-muted-foreground">
              <Check className="mt-1 h-4 w-4 shrink-0 text-sage" />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      );
    case "note":
      return (
        <div className="flex items-start gap-3 rounded-2xl border border-gold/30 bg-gold/10 p-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
          <p className="text-sm leading-relaxed text-foreground/90">{block.text}</p>
        </div>
      );
    case "email":
      return (
        <a
          href={`mailto:${block.email}`}
          data-testid="legal-email"
          className="inline-flex items-center gap-3 rounded-2xl border border-border bg-card/50 px-5 py-4 transition-colors hover:border-gold/40"
        >
          <span className="grid h-10 w-10 place-items-center rounded-xl bg-gold/10 text-gold">
            <Mail className="h-5 w-5" />
          </span>
          <span className="font-display font-semibold text-foreground">{block.email}</span>
        </a>
      );
    case "links":
      return (
        <ul className="grid gap-2 sm:grid-cols-2">
          {block.items.map((l) => (
            <li key={l.href}>
              <a
                href={l.href}
                className="flex items-center justify-between rounded-xl border border-border bg-card/40 px-4 py-3 text-sm font-medium text-foreground/90 transition-colors hover:border-gold/40 hover:text-gold"
              >
                {l.label} <ArrowRight className="h-4 w-4" />
              </a>
            </li>
          ))}
        </ul>
      );
    case "cta":
      return (
        <a
          href={block.href}
          className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition-transform hover:scale-[1.03]"
        >
          {block.label} <ArrowRight className="h-4 w-4" />
        </a>
      );
    default:
      return null;
  }
}
